import {useEffect, useRef} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useLocation, useNavigate} from "react-router-dom";
import {setMessage} from "../app-slice";
import useWorkspaceMembership from "../../models/workspace-membership/use-workspace-membership";
import useEntitySidebars from "./use-entity-sidebars";

// Notification links open exactly what they are about, on any page (task 12):
//   ?task=<documentId>[&comment=…]  → task sidebar      ?goal=<documentId>        → goal sidebar
//   ?project=<documentId>           → project sidebar   ?deliverable=<documentId> → milestone sidebar
//   ?goalUpdate=<documentId>        → goal update view modal  ?goalPeriodReview=1 → end-of-period goals modal
//   ?projectUpdate=<documentId>     → project update view modal
//   ?ws=<workspace documentId>      → switch to that workspace first (a push from another workspace)
// The handled parameters are removed afterwards, so a reload does not open the sidebar again.

const TARGETS = ["task", "goal", "project", "deliverable", "goalUpdate", "goalPeriodReview", "projectUpdate"];
const HANDLED = [...TARGETS, "comment", "ws"];

export function notificationTarget(search) {
    const params = new URLSearchParams(search || "");
    const type = TARGETS.find((key) => params.get(key));
    return {
        workspace: params.get("ws") || null,
        reference: type ? {type, documentId: params.get(type), id: params.get(type), comment: params.get("comment") || null} : null,
    };
}

// workspaceOnly: the mobile shell maps the links to its own detail pages, it only needs the workspace switch
export default function NotificationLinkOpener({workspaceOnly = false}) {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const membershipApi = useWorkspaceMembership();
    const membershipApiRef = useRef(membershipApi);
    membershipApiRef.current = membershipApi;
    const {workspaceMembershipData, workspaceMembershipsData} = useSelector((state) => state.app);
    const activeWorkspace = workspaceMembershipData?.workspace?.documentId || null;
    const entitySidebars = useEntitySidebars();
    const openRef = useRef(entitySidebars.open);
    openRef.current = entitySidebars.open;
    const switchingRef = useRef(null);

    // older notifications linked goals as /goals/<documentId>, which the goal page (numeric id) cannot open
    useEffect(() => {
        const legacyGoal = location.pathname.match(/^\/goals\/([A-Za-z0-9]{16,})$/);
        if (!legacyGoal || workspaceOnly) return;
        const params = new URLSearchParams(location.search);
        params.set("goal", legacyGoal[1]);
        navigate(`/goals?${params.toString()}`, {replace: true});
    }, [location.pathname, location.search, navigate, workspaceOnly]);

    useEffect(() => {
        const target = notificationTarget(location.search);
        const workspace = target.workspace;
        const reference = workspaceOnly ? null : target.reference;
        if ((!workspace && !reference) || !activeWorkspace) return;

        const strip = () => {
            const params = new URLSearchParams(location.search);
            (workspaceOnly ? ["ws"] : HANDLED).forEach((key) => params.delete(key));
            const search = params.toString();
            navigate(`${location.pathname}${search ? `?${search}` : ""}`, {replace: true});
        };

        // a notification from another workspace: switch first, this effect runs again once it is active
        if (workspace && workspace !== activeWorkspace) {
            if (switchingRef.current === workspace) return;
            const membership = (workspaceMembershipsData || []).find((item) => item?.workspace?.documentId === workspace);
            if (!membership) {
                dispatch(setMessage({severity: "warn", content: "به فضای کاری این اعلان دسترسی ندارید."}));
                strip();
                return;
            }
            switchingRef.current = workspace;
            membershipApiRef.current.updateDefaultWorkspaceMembership(membership.documentId || membership.id).finally(() => {
                switchingRef.current = null;
            });
            return;
        }

        let cancelled = false;
        (async () => {
            if (reference) {
                const opened = await openRef.current(reference).catch(() => false);
                if (!opened && !cancelled) {
                    dispatch(setMessage({severity: "warn", content: "این مورد پیدا نشد یا به آن دسترسی ندارید."}));
                }
            }
            if (!cancelled) strip();
        })();
        return () => { cancelled = true; };
        // navigate/dispatch are stable; workspace list changes are picked up through activeWorkspace
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search, activeWorkspace]);

    return workspaceOnly ? null : entitySidebars.sidebars;
}
